var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
/**
 * Redux store for editor-specific Java Lab state.
 */
import { fileMetadataForEditor, updateAllSourceFileOrders, } from '../JavalabFileHelper';
import _ from 'lodash';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { JavalabEditorDialog } from '../types';
var initialSources = {
    'MyClass.java': { text: '', tabOrder: 0, isVisible: true, isValidation: false },
};
var _b = fileMetadataForEditor(initialSources), fileMetadata = _b.fileMetadata, orderedTabKeys = _b.orderedTabKeys, activeTabKey = _b.activeTabKey, lastTabKeyIndex = _b.lastTabKeyIndex;
// exported for testing
export var initialState = {
    fileMetadata: fileMetadata,
    orderedTabKeys: orderedTabKeys,
    activeTabKey: activeTabKey,
    lastTabKeyIndex: lastTabKeyIndex,
    sources: initialSources,
    validation: {},
    editorOpenDialogName: null,
    newFileError: null,
    renameFileError: null,
    editTabKey: null,
    hasCompilationError: false,
};
var javalabEditorSlice = createSlice({
    name: 'javalabEditor',
    initialState: initialState,
    reducers: {
        setSource: {
            reducer: function (state, action) {
                var _a;
                var _b = action.payload, filename = _b.filename, source = _b.source, tabOrder = _b.tabOrder, _c = _b.isVisible, isVisible = _c === void 0 ? true : _c, _d = _b.isValidation, isValidation = _d === void 0 ? false : _d;
                var newSources = __assign(__assign({}, state.sources), (_a = {}, _a[filename] = { text: source, tabOrder: tabOrder, isVisible: isVisible, isValidation: isValidation }, _a));
                state.sources = newSources;
            },
            prepare: function (filename, source, tabOrder, isVisible, isValidation) {
                if (isVisible === void 0) { isVisible = true; }
                if (isValidation === void 0) { isValidation = false; }
                return { payload: { filename: filename, source: source, tabOrder: tabOrder, isVisible: isVisible, isValidation: isValidation } };
            },
        },
        setAllValidation: function (state, action) {
            state.validation = action.payload;
        },
        setAllSourcesAndFileMetadata: {
            reducer: function (state, action) {
                var _a = fileMetadataForEditor(action.payload.sources, action.payload.isEditingStartSources), fileMetadata = _a.fileMetadata, orderedTabKeys = _a.orderedTabKeys, activeTabKey = _a.activeTabKey, lastTabKeyIndex = _a.lastTabKeyIndex;
                var sources = _.cloneDeep(action.payload.sources);
                var updatedSources = updateAllSourceFileOrders(sources, fileMetadata, orderedTabKeys);
                state.sources = updatedSources;
                state.fileMetadata = fileMetadata;
                state.orderedTabKeys = orderedTabKeys;
                state.activeTabKey = activeTabKey;
                state.lastTabKeyIndex = lastTabKeyIndex;
            },
            prepare: function (sources, isEditingStartSources) {
                return { payload: { sources: sources, isEditingStartSources: isEditingStartSources } };
            },
        },
        renameFile: {
            reducer: function (state, action) {
                var source = state.sources[action.payload.oldFilename];
                // if the old filename doesn't exist, this is a no-op
                if (source !== undefined) {
                    var newSources = __assign({}, state.sources);
                    delete newSources[action.payload.oldFilename];
                    newSources[action.payload.newFilename] = source;
                    state.sources = newSources;
                }
            },
            prepare: function (oldFilename, newFilename) {
                return { payload: { oldFilename: oldFilename, newFilename: newFilename } };
            },
        },
        sourceTextUpdated: {
            reducer: function (state, action) {
                state.sources[action.payload.filename].text = action.payload.text;
            },
            prepare: function (filename, text) {
                return { payload: { filename: filename, text: text } };
            },
        },
        sourceVisibilityUpdated: {
            reducer: function (state, action) {
                state.sources[action.payload.filename].isVisible =
                    action.payload.isVisible;
            },
            prepare: function (filename, isVisible) {
                return { payload: { filename: filename, isVisible: isVisible } };
            },
        },
        sourceValidationUpdated: {
            reducer: function (state, action) {
                state.sources[action.payload.filename].isValidation =
                    action.payload.isValidation;
            },
            prepare: function (filename, isValidation) {
                return { payload: { filename: filename, isValidation: isValidation } };
            },
        },
        sourceFileOrderUpdated: function (state) {
            var sources = _.cloneDeep(state.sources);
            var updatedSources = updateAllSourceFileOrders(sources, state.fileMetadata, state.orderedTabKeys);
            state.sources = updatedSources;
        },
        removeFile: function (state, action) {
            var filename = action.payload;
            var newSources = __assign({}, state.sources);
            delete newSources[filename];
            state.sources = newSources;
        },
        openEditorDialog: function (state, action) {
            if (Object.values(JavalabEditorDialog).includes(action.payload)) {
                state.editorOpenDialogName = action.payload;
            }
        },
        closeEditorDialog: function (state) {
            state.editorOpenDialogName = null;
        },
        setEditTabKey: function (state, action) {
            state.editTabKey = action.payload;
        },
        setActiveTabKey: function (state, action) {
            state.activeTabKey = action.payload;
        },
        setFileMetadata: function (state, action) {
            state.fileMetadata = action.payload;
        },
        setOrderedTabKeys: function (state, action) {
            state.orderedTabKeys = action.payload;
        },
        setAllEditorMetadata: {
            reducer: function (state, action) {
                state.fileMetadata = action.payload.fileMetadata;
                state.orderedTabKeys = action.payload.orderedTabKeys;
                state.activeTabKey = action.payload.activeTabKey;
                state.lastTabKeyIndex =
                    action.payload.lastTabKeyIndex || state.lastTabKeyIndex;
            },
            prepare: function (fileMetadata, orderedTabKeys, activeTabKey, lastTabKeyIndex) {
                return {
                    payload: {
                        fileMetadata: fileMetadata,
                        orderedTabKeys: orderedTabKeys,
                        activeTabKey: activeTabKey,
                        lastTabKeyIndex: lastTabKeyIndex,
                    },
                };
            },
        },
        setNewFileError: function (state, action) {
            state.newFileError = action.payload;
        },
        clearNewFileError: function (state) {
            state.newFileError = null;
        },
        setRenameFileError: function (state, action) {
            state.renameFileError = action.payload;
        },
        clearRenameFileError: function (state) {
            state.renameFileError = null;
        },
        setHasCompilationError: function (state, action) {
            state.hasCompilationError = action.payload;
        },
    },
});
// Selectors
var selectSources = function (state) {
    return state.javalabEditor.sources;
};
export var getSources = createSelector(selectSources, function (sources) {
    var result = {};
    for (var key in sources) {
        if (!sources[key].isValidation) {
            result[key] = {
                text: sources[key].text,
                isVisible: sources[key].isVisible,
                tabOrder: sources[key].tabOrder,
            };
        }
    }
    return result;
});
export var getValidation = createSelector(selectSources, function (sources) {
    var validation = {};
    for (var key in sources) {
        if (sources[key].isValidation) {
            validation[key] = {
                text: sources[key].text,
                tabOrder: sources[key].tabOrder,
            };
        }
    }
    return validation;
});
// Exports
export var setSource = (_a = javalabEditorSlice.actions, _a.setSource), setAllValidation = _a.setAllValidation, setAllSourcesAndFileMetadata = _a.setAllSourcesAndFileMetadata, renameFile = _a.renameFile, sourceTextUpdated = _a.sourceTextUpdated, sourceVisibilityUpdated = _a.sourceVisibilityUpdated, sourceValidationUpdated = _a.sourceValidationUpdated, sourceFileOrderUpdated = _a.sourceFileOrderUpdated, removeFile = _a.removeFile, openEditorDialog = _a.openEditorDialog, closeEditorDialog = _a.closeEditorDialog, setEditTabKey = _a.setEditTabKey, setActiveTabKey = _a.setActiveTabKey, setFileMetadata = _a.setFileMetadata, setOrderedTabKeys = _a.setOrderedTabKeys, setAllEditorMetadata = _a.setAllEditorMetadata, setRenameFileError = _a.setRenameFileError, clearRenameFileError = _a.clearRenameFileError, setNewFileError = _a.setNewFileError, clearNewFileError = _a.clearNewFileError, setHasCompilationError = _a.setHasCompilationError;
export default javalabEditorSlice.reducer;
